import { useWindow } from '@src/hooks/useWindow';
import { useState, useCallback, useEffect, useMemo, useLayoutEffect } from 'react';
export const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;
export interface SnapCarouselResult {
  readonly activeItemIndex: number;
  readonly activePageIndex: number;
  readonly goToItem: (itemIndex: number, isAuto?: boolean) => void;
  readonly goToPage: (pageIndex: number, isAuto?: boolean) => void;
  readonly hasUserClicked: boolean;
  readonly items: number[];
  readonly itemsPercent: number;
  readonly nextItem: (isAuto?: boolean) => void;
  readonly nextPage: (isAuto?: boolean) => void;
  readonly pages: number[][];
  readonly pagesPercent: number;
  readonly prevItem: (isAuto?: boolean) => void;
  readonly prevPage: (isAuto?: boolean) => void;
  readonly refresh: () => void;
  readonly scrollRef: (el: HTMLElement | null) => void;
  readonly snapPointIndexes: Set<number>;
}
export interface SnapCarouselOptions {
  readonly axis?: 'x' | 'y';
  readonly initialPages?: number[][];
  readonly initialItems?: number[];
}
interface SnapCarouselState {
  readonly activeItemIndex: number;
  readonly activePageIndex: number;
  readonly items: number[];
  readonly pages: number[][];
}
export const useSnapCarousel = ({
  axis = 'x',
  initialPages = [],
  initialItems = []
}: SnapCarouselOptions = {}): SnapCarouselResult => {
  const clientDimension = axis === 'x' ? 'clientWidth' : 'clientHeight';
  const dimension = axis === 'x' ? 'width' : 'height';
  const farSidePos = axis === 'x' ? 'right' : 'bottom';
  const nearSidePos = axis === 'x' ? 'left' : 'top';
  const scrollDimension = axis === 'x' ? 'scrollWidth' : 'scrollHeight';
  const scrollPos = axis === 'x' ? 'scrollLeft' : 'scrollTop';
  const windowProxy = useWindow();
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const [hasUserClicked, setHasUserClicked] = useState<boolean>(false);
  const [{
    activeItemIndex,
    activePageIndex,
    items,
    pages
  }, setCarouselState] = useState<SnapCarouselState>({
    pages: initialPages,
    items: initialItems,
    activePageIndex: 0,
    activeItemIndex: 0
  });
  const refreshActivePage = useCallback((pages_0: number[][]) => {
    if (!scrollEl) {
      return;
    }
    const items_0 = Array.from(scrollEl.children);

    // https://excalidraw.com/#json=1ztbZ26T3ri14SiJBZlt4,Rqa2mjiaYJesnfPYEiBdPQ
    const hasScrolledToEnd = Math.floor(scrollEl[scrollDimension] - scrollEl[scrollPos]) <= scrollEl[clientDimension];
    if (hasScrolledToEnd) {
      // If scrolled to the end, set page to last as it may not end up with an
      // offset of 0 due to scroll capping.
      // (it's not quite aligned with how snapping works, but good enough for now)
      const lastPage = pages_0[pages_0.length - 1] || [];
      setCarouselState({
        activeItemIndex: lastPage[0],
        activePageIndex: activePageIndex === 0 ? pages_0.length - 1 : activePageIndex,
        pages: pages_0,
        items: items_0.map((_, i) => i)
      });
      return;
    }
    const scrollPort = scrollEl.getBoundingClientRect();
    const itemOffsets = items_0.map(item => {
      assert(item instanceof HTMLElement, 'Expected HTMLElement');
      const scrollSpacing = getEffectiveScrollSpacing(windowProxy, scrollEl, item, nearSidePos);
      const rect = item.getBoundingClientRect();
      const offset = rect[nearSidePos] - scrollPort[nearSidePos] - scrollSpacing;
      return Math.abs(offset);
    });
    // console.log({ itemOffsets })
    const minItemOffset = Math.min(...itemOffsets);
    const nextActiveItemIndex = itemOffsets.indexOf(minItemOffset);
    const pageOffsets = pages_0.map(page => {
      const leadIndex = page[0];
      const leadEl = items_0[leadIndex];
      assert(leadEl instanceof HTMLElement, 'Expected HTMLElement');
      const scrollSpacing_0 = getEffectiveScrollSpacing(windowProxy, scrollEl, leadEl, nearSidePos);
      const rect_0 = leadEl.getBoundingClientRect();
      const offset_0 = rect_0[nearSidePos] - scrollPort[nearSidePos] - scrollSpacing_0;
      return Math.abs(offset_0);
    });
    // console.log({ pageOffsets })
    const minPageOffset = Math.min(...pageOffsets);
    const nextActivePageIndex = pageOffsets.indexOf(minPageOffset);
    setCarouselState({
      activeItemIndex: nextActiveItemIndex,
      activePageIndex: nextActivePageIndex,
      items: items_0.map((__0, i_0) => i_0),
      pages: pages_0
    });
  }, [scrollEl, clientDimension, nearSidePos, scrollDimension, scrollPos]);
  const refresh = useCallback(() => {
    if (!scrollEl) {
      return;
    }
    const items_1 = Array.from(scrollEl.children);
    const scrollPort_0 = scrollEl.getBoundingClientRect();
    let currPageStartPos: number;
    const pages_1 = items_1.reduce<number[][]>((acc, item_0, i_1) => {
      assert(item_0 instanceof HTMLElement, 'Expected HTMLElement');
      const currPage = acc[acc.length - 1];
      const rect_1 = getOffsetRect(item_0, item_0.parentElement);
      if (!currPage || rect_1[farSidePos] - currPageStartPos > Math.ceil(scrollPort_0[dimension])) {
        acc.push([i_1]);
        const scrollSpacing_1 = getEffectiveScrollSpacing(windowProxy, scrollEl, item_0, nearSidePos);
        currPageStartPos = rect_1[nearSidePos] - scrollSpacing_1;
      } else {
        currPage.push(i_1);
      }
      return acc;
    }, []);
    refreshActivePage(pages_1);
  }, [refreshActivePage, scrollEl, dimension, farSidePos, nearSidePos]);
  useIsomorphicLayoutEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    refresh();
  }, [items.length, refresh]);

  // On resize we need to refresh the state
  useEffect(() => {
    const handle = () => {
      refresh();
    };
    windowProxy.addEventListener('resize', handle);
    windowProxy.addEventListener('orientationchange', handle);
    return () => {
      windowProxy.removeEventListener('resize', handle);
      windowProxy.removeEventListener('orientationchange', handle);
    };
  }, [refresh, items.length]);

  // On scroll we only need to refresh the current page as it won't impact `pages`.
  useEffect(() => {
    if (!scrollEl) {
      return;
    }
    const handle_0 = () => {
      // TODO: Consider debouncing / throttling
      refreshActivePage(pages);
    };
    scrollEl.addEventListener('scroll', handle_0);
    return () => {
      scrollEl.removeEventListener('scroll', handle_0);
    };
  }, [refreshActivePage, pages, scrollEl]);
  const handleGoToItem = (goToIndex: number, isAuto?: boolean) => {
    if (!isAuto) {
      setHasUserClicked(true);
    }
    let index = goToIndex;
    if (goToIndex < 0) {
      index = items.length - 1;
    }
    if (goToIndex > items.length - 1) {
      index = 0;
    }
    if (!scrollEl) {
      return;
    }

    // console.log({ activeItemIndex, goToIndex, index })

    const itemEl = Array.from(scrollEl.children)[index];
    if (!itemEl) {
      return;
    }
    const scrollSpacing_2 = getEffectiveScrollSpacing(windowProxy, scrollEl, itemEl as HTMLElement, nearSidePos);
    // NOTE: I've tried `leadEl.scrollIntoView` but it often fails in chrome on Mac OS.
    scrollEl.scrollTo({
      behavior: 'smooth',
      [nearSidePos]: getOffsetRect(itemEl, itemEl.parentElement)[nearSidePos] - scrollSpacing_2
    });
  };
  const handleGoToPage = (goToIndex_0: number, isAuto_0?: boolean) => {
    if (!isAuto_0) {
      setHasUserClicked(true);
    }
    let index_0 = goToIndex_0;
    if (goToIndex_0 < 0) {
      index_0 = pages.length - 1;
    }
    if (goToIndex_0 > pages.length - 1) {
      index_0 = 0;
    }

    // console.log({ activePageIndex, goToIndex, index })

    if (!scrollEl) {
      return;
    }
    const page_0 = pages[index_0];
    if (!page_0) {
      return;
    }
    const items_2 = Array.from(scrollEl.children);
    const leadIndex_0: number | undefined = page_0[0];
    const leadEl_0: Element | undefined = items_2[leadIndex_0];
    if (!(leadEl_0 instanceof HTMLElement)) {
      return;
    }
    const scrollSpacing_3 = getEffectiveScrollSpacing(windowProxy, scrollEl, leadEl_0, nearSidePos);
    // NOTE: I've tried `leadEl.scrollIntoView` but it often fails in chrome on Mac OS.
    scrollEl.scrollTo({
      behavior: 'smooth',
      [nearSidePos]: getOffsetRect(leadEl_0, leadEl_0.parentElement)[nearSidePos] - scrollSpacing_3
    });
  };
  const handlePrevItem = (isAuto_1 = false) => {
    handleGoToItem(activeItemIndex - 1, isAuto_1);
  };
  const handlePrevPage = (isAuto_2 = false) => {
    handleGoToPage(activePageIndex - 1, isAuto_2);
  };
  const handleNextPage = (isAuto_3 = false) => {
    handleGoToPage(activePageIndex + 1, isAuto_3);
  };
  const handleNextItem = (isAuto_4 = false) => {
    handleGoToItem(activeItemIndex + 1, isAuto_4);
  };
  const snapPointIndexes = useMemo(() => new Set(pages.map(page_1 => page_1[0])), [pages, items]);
  return {
    activeItemIndex,
    activePageIndex,
    goToItem: handleGoToItem,
    goToPage: handleGoToPage,
    hasUserClicked,
    items,
    itemsPercent: Math.round(100 * (activeItemIndex + 1) / items.length),
    nextItem: handleNextItem,
    nextPage: handleNextPage,
    pages,
    pagesPercent: Math.round(100 * (activePageIndex + 1) / pages.length),
    prevItem: handlePrevItem,
    prevPage: handlePrevPage,
    refresh,
    scrollRef: setScrollEl,
    snapPointIndexes
  };
};

// Like `el.getBoundingClientRect()` but ignores scroll.
// It's similar to `offsetLeft` / `offsetTop`, but offers some of the virtues of `getBoundingClientRect`
// such as factoring in CSS transforms & handling wrapped inline elements.
// https://codepen.io/riscarrott/pen/ZEjyyay
// https://w3c.github.io/csswg-drafts/cssom-view/#dom-htmlelement-offsetleft
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetLeft
const getOffsetRect = (el: Element, relativeTo?: Element | null) => {
  const rect = _getOffsetRect(el);
  if (!relativeTo) {
    return rect;
  }
  const relativeRect = _getOffsetRect(relativeTo);
  return {
    left: rect.left - relativeRect.left,
    top: rect.top - relativeRect.top,
    right: rect.right - relativeRect.left,
    bottom: rect.bottom - relativeRect.top,
    width: rect.width,
    height: rect.height
  };
};
const _getOffsetRect = (el: Element) => {
  if (!el?.getBoundingClientRect) {
    return {};
  }
  const rect = el.getBoundingClientRect();
  let scrollLeft = 0;
  let scrollTop = 0;
  let parentEl = el.parentElement;
  while (parentEl) {
    scrollLeft += parentEl.scrollLeft;
    scrollTop += parentEl.scrollTop;
    parentEl = parentEl.parentElement;
  }
  const left = rect.left + scrollLeft;
  const top = rect.top + scrollTop;
  return {
    left,
    top,
    right: left + rect.width,
    bottom: top + rect.height,
    width: rect.width,
    height: rect.height
  };
};

//  `window.getComputedStyle` gives us the *computed* value for scroll-padding-* so we have
// to convert it to the used value (i.e. px value) ourselves :(
// https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-padding
const getScrollPaddingUsedValue = (windowProxy: Window, el: HTMLElement, pos: 'left' | 'top') => {
  if (!windowProxy?.getComputedStyle) {
    return;
  }
  const style = windowProxy.getComputedStyle(el);
  const scrollPadding = style.getPropertyValue(`scroll-padding-${pos}`);
  if (scrollPadding === 'auto') {
    return 0;
  }
  // https://developer.mozilla.org/en-US/docs/Web/CSS/length
  // https://www.w3.org/TR/css3-values/#length-value
  const invalidMsg = `Unsupported scroll padding value, expected <length> or <percentage> value, received ${scrollPadding}`;
  if (scrollPadding.endsWith('px')) {
    const value = parseInt(scrollPadding);
    assert(!Number.isNaN(value), invalidMsg);
    return value;
  }
  if (scrollPadding.endsWith('%')) {
    const value = parseInt(scrollPadding);
    assert(!Number.isNaN(value), invalidMsg);
    return el.clientWidth / 100 * value;
  }
  // e.g. calc(10% + 10px) // NOTE: We could in theory resolve this...
  // throw new ReactSnapCarouselError(invalidMsg)
};

// Unlike scroll-padding, scroll-margin doesn't support <percentage> values, so we should always
// get back a px value, meaning it's effectively already the *used* value.
// https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin
const getScrollMarginUsedValue = (windowProxy: Window, el: HTMLElement, pos: 'left' | 'top') => {
  if (!windowProxy?.getComputedStyle) {
    return;
  }
  const style = windowProxy.getComputedStyle(el);
  const scrollMargin = style.getPropertyValue(`scroll-margin-${pos}`);

  // https://developer.mozilla.org/en-US/docs/Web/CSS/length
  // https://www.w3.org/TR/css3-values/#length-value
  const invalidMsg = `Unsupported scroll margin value, expected <length> value, received ${scrollMargin}`;
  assert(scrollMargin.endsWith('px'), invalidMsg); // Even scroll-margin: 0 should return "0px"
  const value = parseInt(scrollMargin);
  assert(!Number.isNaN(value), invalidMsg);
  return value;
};

// The 'effective' scroll spacing is the actual scroll padding + margin that will be used for a
// given item after factoring in whether there is enough scroll width available.
const getEffectiveScrollSpacing = (windowProxy: Window, scrollEl: HTMLElement, itemEl: HTMLElement, pos: 'left' | 'top') => {
  if (!windowProxy) {
    return;
  }
  const scrollPadding = getScrollPaddingUsedValue(windowProxy, scrollEl, pos);
  const scrollMargin = getScrollMarginUsedValue(windowProxy, itemEl, pos);
  const rect = getOffsetRect(itemEl, itemEl.parentElement);
  return Math.min(scrollPadding + scrollMargin, rect[pos]);
};
function assert(value: any, message: string): asserts value {
  if (value) {
    return;
  }
  const error = new ReactSnapCarouselError(message);
  console.error(error);
  // throw error
}
class ReactSnapCarouselError extends Error {
  constructor(message: string) {
    super(`[usSnapCarousel]: ${message}`);
  }
}